import { useEffect, useRef } from 'react';
import { Edge, Node } from 'reactflow';
import { isMigratedNode } from '../constants/migratedNodes';
import { ProcessService } from '../service/processService';
import { nodeDataAutoSaveDynamic } from '../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../util/checkExecute';

interface UseTemporalFlowExecutionParams {
  id: string;
  data: Record<string, any>;
  nodeType: string;
  edges: Edge[];
  flowId: string;
  sessionId: string;
  setNodeState: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void;
  setSaving: (value: boolean) => void;
  setSchema: (schema: any) => void;
  setIsLoading: (value: boolean) => void;
}

export function useTemporalFlowExecution({
  id,
  data,
  nodeType,
  edges,
  flowId,
  sessionId,
  setNodeState,
  setSaving,
  setSchema,
  setIsLoading,
}: UseTemporalFlowExecutionParams) {
  const migrated = isMigratedNode(nodeType);
  const lastProcessedTriggerId = useRef<string | null>(null);
  const lastCompletedTriggerId = useRef<string | null>(null);

  useEffect(() => {
    if (!migrated) return;
    if (!data?.flowTrigger?.id) return;
    if (data?.canceled || data?.paused) return;
    if (lastProcessedTriggerId.current === data.flowTrigger.id) return;
    lastProcessedTriggerId.current = data.flowTrigger.id;
    setIsLoading(true);
    ProcessService.response({
      nodeId: id,
      sessionId,
      flowId,
    }).catch(error => {
      console.error('Temporal flow execution failed', { nodeId: id, error });
      setIsLoading(false);
    });
  }, [
    migrated,
    data?.flowTrigger?.id,
    data?.canceled,
    data?.paused,
    id,
    sessionId,
    flowId,
    setIsLoading,
  ]);

  useEffect(() => {
    if (!migrated) return;
    if (!data?.flowTrigger?.id) return;
    const executionStatus = data?.executionStatus as string | undefined;
    if (!executionStatus) return;
    if (lastCompletedTriggerId.current === data.flowTrigger.id) return;
    if (executionStatus === 'COMPLETED') {
      lastCompletedTriggerId.current = data.flowTrigger.id;
      setIsLoading(false);
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowTriggerData: checkExecuteTrigger(data, id),
        changeType: 'execute',
        flowId,
        setSchema,
      });
    }
    if (executionStatus === 'FAILED') {
      lastCompletedTriggerId.current = data.flowTrigger.id;
      setIsLoading(false);
    }
  }, [
    migrated,
    data?.flowTrigger?.id,
    data?.executionStatus,
    id,
    edges,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
    setIsLoading,
  ]);

  return { isMigratedNode: migrated };
}
